import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FinanceKeys } from "web_myplann/Api/Finances/financeKeys";
import type { ExpensesQueryData } from "web_myplann/Api/Finances/useExpenseQuery";
import {
	type ClientIncomeDetailResponseDTO,
	type FinancialPlanUpdateRequestDTO,
	type FinancialPlanUpdateResponseDTO,
	patchClientFinancialPlanV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type IncomesQueryData = {
	data: ClientIncomeDetailResponseDTO[];
};

export const usePatchFinanceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse(
			(requestContract: FinancialPlanUpdateRequestDTO) =>
				patchClientFinancialPlanV2(requestContract),
		),
		onMutate: async (requestContract: FinancialPlanUpdateRequestDTO) => {
			await queryClient.cancelQueries({ queryKey: FinanceKeys.expense() });
			await queryClient.cancelQueries({ queryKey: FinanceKeys.income() });

			const previousExpenses = queryClient.getQueryData<ExpensesQueryData>(
				FinanceKeys.expense(),
			);
			const previousIncomes = queryClient.getQueryData<IncomesQueryData>(
				FinanceKeys.income(),
			);

			if (requestContract.expenses.length > 0 && previousExpenses) {
				const updatedExpenses = previousExpenses.data.map((expense) => {
					const update = requestContract.expenses.find(
						(requestExpense) =>
							requestExpense.id != null && requestExpense.id === expense.id,
					);
					return update
						? {
								...expense,
								name: update.name,
								type: update.type,
								monthlyPayment: update.monthlyPayment,
								sustainableAmount: update.sustainableAmount,
							}
						: expense;
				});

				queryClient.setQueryData<ExpensesQueryData>(FinanceKeys.expense(), {
					...previousExpenses,
					data: updatedExpenses,
				});
			}

			if (requestContract.incomes.length > 0 && previousIncomes) {
				const updatedIncomes = previousIncomes.data.map((income) => {
					const update = requestContract.incomes.find(
						(requestIncome) =>
							requestIncome.id != null && requestIncome.id === income.id,
					);
					return update
						? {
								...income,
								name: update.name,
								amount: update.amount,
							}
						: income;
				});

				queryClient.setQueryData<IncomesQueryData>(FinanceKeys.income(), {
					...previousIncomes,
					data: updatedIncomes,
				});
			}

			return { previousExpenses, previousIncomes };
		},
		onError: (_error, _variables, context) => {
			if (context?.previousExpenses) {
				queryClient.setQueryData(
					FinanceKeys.expense(),
					context.previousExpenses,
				);
			}
			if (context?.previousIncomes) {
				queryClient.setQueryData(FinanceKeys.income(), context.previousIncomes);
			}
		},
		onSuccess: (response: FinancialPlanUpdateResponseDTO | undefined) => {
			if (!response) {
				return;
			}
			queryClient.setQueryData<IncomesQueryData>(FinanceKeys.income(), {
				data: response.incomes,
			});
			queryClient.setQueryData<ExpensesQueryData>(
				FinanceKeys.expense(),
				(prev) => ({
					meta: { ...prev?.meta, totalCount: response.expenses.length },
					data: response.expenses,
				}),
			);
		},
	});
};
